import { memo, useMemo } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, type SelectTriggerProps } from '@air/primitive-select';
import { cn } from '@air/utils-tailwind';

import { type FilterLogicType } from '~/store/filters/types';

export type FilterLogicSelectProps = {
  selectedLogic: FilterLogicType;
  onLogicChange: (selected: FilterLogicType) => void;
  logicOptions?: FilterLogicType[];
} & Pick<SelectTriggerProps, 'className'>;

type LogicOption = {
  value: FilterLogicType;
  label: string;
};

const allLogicOptions: FilterLogicType[] = ['and', 'or', 'not'];

export const FilterLogicSelect = memo(
  ({ selectedLogic, onLogicChange, logicOptions = allLogicOptions, className }: FilterLogicSelectProps) => {
    const logicSelectOptions = useMemo<LogicOption[]>(() => {
      return [
        { value: 'and', label: 'Contains' },
        { value: 'or', label: 'Has one of' },
        { value: 'not', label: 'Does not contain' },
      ];
    }, []);

    const availableOptions = useMemo(
      () => logicSelectOptions.filter((option) => logicOptions.includes(option.value)),
      [logicOptions, logicSelectOptions],
    );

    return (
      <Select onValueChange={onLogicChange} value={selectedLogic}>
        <SelectTrigger
          className={cn('mb-1 w-full', 'w-auto border-0 font-medium', className)}
          placeholder="Select logic"
          size="small"
        />
        <SelectContent className="min-w-[304px]" sideOffset={4}>
          {availableOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);

FilterLogicSelect.displayName = 'FilterLogicSelect';
